import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Cookies from 'js-cookie';
import ChatView from '../pages/Cases/ChatTab/ChatView';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL, timeout: 20000 });
const config = () => ({ headers: { Authorization: `Bearer ${Cookies.get('token')}` } });

export default function ServerChat({ caseDetail, loadingCaseDetail }) {
  const agent = useSelector(state => state.agentProfile?.agentUser?.agent);
  const threadId = caseDetail?.case?.comm_thread_id;
  const caseId = caseDetail?.case?.id;
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const pending = useRef(null);
  const refresh = useCallback(async () => {
    const result = await api.get('/chat/list-messages', { ...config(), params: { threadId } });
    if (!result.data.ok) throw new Error('Unable to load chat.');
    return result.data.messages || [];
  }, [threadId]);
  useEffect(() => {
    if (!threadId) return;
    let alive = true;
    const load = async () => {
      try {
        const rows = await refresh();
        if (!alive) return;
        setMessages(rows); setError('');
        // Only acknowledge a received message after it is visible.
        const received = rows.find(row => row.senderType === 'client');
        if (received && document.visibilityState !== 'hidden')
          await api.post('/chat/mark-as-read', { messageId: received.id }, config()).catch(() => {});
      } catch { if (alive) { setMessages(null); setError('Unable to load chat. Access may have changed.'); } }
    };
    load();
    const timer = setInterval(() => { if (document.visibilityState !== 'hidden') load(); }, 5000);
    return () => { alive = false; clearInterval(timer); };
  }, [refresh, threadId]);
  const send = async event => {
    event.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    if (!pending.current || pending.current.content.message !== text.trim())
      pending.current = { id: crypto.randomUUID(), content: { message: text.trim() }, threadId, caseId };
    try {
      const result = await api.post('/chat/send-message', pending.current, config());
      if (!result.data.ok) throw new Error('Send failed');
      pending.current = null; setText(''); setError(''); setMessages(await refresh());
    } catch { setError('Unable to confirm sending. Retry to check or send this message.'); }
    finally { setSending(false); }
  };
  if (!threadId) return <Typography>Chat is unavailable for this case.</Typography>;
  return <>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    <ChatView caseDetail={caseDetail} loadingCaseDetail={loadingCaseDetail} agent={agent}
      messages={messages} loading={!messages && !error} text={text} onTextChange={setText}
      onSubmit={send} sending={sending} />
  </>;
}
